import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  StaggerCss,
  getNinePointLayoutProperties,
} from '@mentimeter/question-modules-shared';
import { FlexibleText } from '@mentimeter/slides-presentation-ui/text/flexible-text';
import { Label } from '@mentimeter/slides-presentation-ui/title/label';
import React from 'react';
import { alpha } from '@mentimeter/ragnar-colors';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { MarkdownText } from '@mentimeter/slides-presentation-ui/text/markdown-text';

export function QuotePresentation() {
  const {
    Utils: { fontOffset },
    useQuestion,
    useConfig,
  } = usePresentationContext();

  const question = useQuestion();
  const title = question.question;
  const { enableAnimations } = useConfig();

  // for font-size calculations
  const breakpoints = [
    { lineCount: 8, offset: -4 },
    { lineCount: 6, offset: -2 },
    { lineCount: 4, offset: -1 },
  ];
  const layoutType = question.layout?.type ?? 'default';
  const { textAlign, justifyContent, alignItems } =
    getNinePointLayoutProperties(question.layout);
  // quote font size
  const width = layoutType === 'default' || layoutType === 'full-bg' ? 30 : 20;
  const height = Math.ceil(title.length / width);
  const offset =
    fontOffset({ height, width, breakpoints }) +
    (question.layout?.font_size_offset ?? 0);

  return (
    <Box
      maxWidth="85%"
      height="100%"
      alignItems={alignItems}
      justifyContent={justifyContent}
    >
      <StaggerCss index={1} height="auto" enableAnimations={enableAnimations}>
        {question.title_meta && (
          <Label>
            <MarkdownText
              disableLineBreaks
              source={question.title_meta}
              allowHyperlinks
            />
          </Label>
        )}
        <Box
          as="blockquote"
          width="100%"
          alignItems={alignItems}
          justifyContent={justifyContent}
          mt={5}
        >
          <Text
            aria-hidden
            fontSize="600px"
            fontFamily="heading"
            fontWeight="semiBold"
            color="text"
            lineHeight="none"
            mb="-500px"
            mt="-120px"
            extend={({ theme }) => ({
              color: alpha(theme.colors.primary, 0.25),
              fontFamily: 'arial',
            })}
          >
            &ldquo;
          </Text>
          <FlexibleText
            as="p"
            fontWeight="light"
            fontOffset={offset}
            fontSize={13}
            fontFamily="heading"
            lineHeight="heading"
            textAlign={textAlign}
            color="text"
          >
            <MarkdownText
              source={title}
              disableLineBreaks={false}
              allowBlankLines
              allowHyperlinks
            />
          </FlexibleText>
        </Box>
      </StaggerCss>
      {question.question_description && (
        <StaggerCss index={2} height="auto" enableAnimations={enableAnimations}>
          <Box
            width="100%"
            alignItems={alignItems}
            justifyContent={justifyContent}
          >
            <Text
              as="p"
              color="text"
              fontSize="225"
              fontStyle="italic"
              mb="-50px"
              mt={5}
              textAlign={textAlign}
            >
              <MarkdownText
                source={`– ${question.question_description}`}
                disableLineBreaks
                allowHyperlinks
              />
            </Text>
          </Box>
        </StaggerCss>
      )}
    </Box>
  );
}
